import type { ProductType } from '@/api/models';

/**
 * A min/max filter (BOS-203). Either end may be open (`null`), and both ends
 * are included, as bos-api reads them (BOS-174). A money range's ends are
 * integer tetri; a quantity range's are units, which may be fractional.
 */
export type ProductListRange = {
    min: number | null;
    max: number | null;
};

/** The range filters, named as their filter groups are. */
export type ProductRangeFilter = 'price' | 'cost' | 'quantity';

/** A range's two ends, in the order the drawer lays them out. */
export const PRODUCT_RANGE_ENDS = ['min', 'max'] as const;

export type ProductRangeEnd = (typeof PRODUCT_RANGE_ENDS)[number];

/** A range with both ends open: no filter. */
export const OPEN_PRODUCT_LIST_RANGE: Readonly<ProductListRange> =
    Object.freeze({ min: null, max: null });

/**
 * A minimum above its maximum: a range nothing can fall in, which bos-api
 * refuses with a 400 rather than answering with an empty page.
 */
export const isProductListRangeInverted = (range: ProductListRange): boolean =>
    range.min !== null && range.max !== null && range.min > range.max;

/**
 * The Products list's applied filters and paging, kept in the page address
 * (BOS-196) so refresh, back/forward and a shared link all reopen the same
 * list. The address is the single source of truth: the page hook reads it
 * through `readProductListFilters` and writes it through
 * `writeProductListFilters`, and nothing else touches these keys.
 *
 * Reading is where the address is validated. It is typed by hand and pasted
 * around, so a value that isn't one the list could have written falls back to
 * its default instead of reaching the request (a non-numeric `page` would be a
 * 400 from the server's `int` binding, and the page would fail over a link).
 */
export type ProductListFilters = {
    /** Trimmed; `''` is no search. */
    search: string;
    type: ProductType | null;
    /**
     * Any of these categories, each with everything beneath it (bos-api
     * BOS-173), in the order they were picked; no repeats.
     */
    categoryIds: string[];
    /** Products with no category match too, alongside `categoryIds` (BOS-202). */
    noCategory: boolean;
    /** Any of these brands, in the order they were picked; no repeats. */
    brandIds: string[];
    /** Products with no brand match too, alongside `brandIds` (BOS-201). */
    noBrand: boolean;
    lowStock: boolean;
    /** Reveal archived products, which the catalog hides by default (BOS-121). */
    includeArchived: boolean;
    /**
     * Only drafts: products an import could not finish, in the catalog but
     * not for sale (bos-app#226). Off lists drafts and the rest together, as
     * the catalog does by default.
     */
    draft: boolean;
    /** Any variant's sale price, in tetri (BOS-203). */
    price: ProductListRange;
    /** Any variant's current cost, in tetri; an unknown cost never matches. */
    cost: ProductListRange;
    /** The product's total on hand, expired stock included. */
    quantity: ProductListRange;
    /**
     * The branch whose stock the figures and the stock filters count
     * (bos-app#118), or `null` for the caller's own scope: every branch for an
     * OrgAdmin. Only an OrgAdmin picks one; it moves the figures and never
     * hides a product.
     */
    branchId: string | null;
    /** 1-based. */
    page: number;
    /** One of `PRODUCT_PAGE_SIZE_OPTIONS`. */
    pageSize: number;
};

export type ProductListDrawerFilters = Pick<
    ProductListFilters,
    | 'type'
    | 'categoryIds'
    | 'noCategory'
    | 'brandIds'
    | 'noBrand'
    | 'lowStock'
    | 'includeArchived'
    | 'draft'
    | ProductRangeFilter
    | 'branchId'
>;

export type ProductFilterGroup =
    | 'branch'
    | 'category'
    | 'brand'
    | 'type'
    | ProductRangeFilter
    | 'lowStock'
    | 'archived'
    | 'draft';
